(() => {
  'use strict';
  if (!customElements.get('sky-slideshow')) customElements.define('sky-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.index = 0;
      this.animations = [];
      this.transitionId = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || this.dataset.autoplay !== 'true';
      this.visible = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) this.start(); else { this.stop(); this.finishTransition(); }
        });
        this.visibilityObserver.observe(this);
      }
      this.pauseButton = this.querySelector('[data-pause]');
      this.dots.forEach(dot => dot.addEventListener('click', () => this.go(Number(dot.dataset.go)), options));
      this.pauseButton?.addEventListener('click', () => { this.paused = !this.paused; this.updatePause(); this.start(); }, options);
      this.addEventListener('focusin', e => { if (e.target.closest('.sg-slides')) this.stop(); }, options);
      this.addEventListener('focusout', e => { if (!this.contains(e.relatedTarget)) this.start(); }, options);
      this.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + ((e.key === 'ArrowLeft') === (document.documentElement.dir === 'rtl') ? 1 : -1)); }
      }, options);
      this.addEventListener('touchstart', e => {this.touchX = e.changedTouches[0].clientX;this.stop();}, {...options,passive:true});
      this.addEventListener('touchend', e => {const delta=e.changedTouches[0].clientX-this.touchX;const rtl=getComputedStyle(this).direction==='rtl';if(Math.abs(delta)>45)this.go(this.index+((delta>0)===rtl?1:-1));this.start();}, {...options,passive:true});
      document.addEventListener('visibilitychange', () => {if(document.hidden){this.finishTransition();this.stop();}else this.start();}, options);
      this.motion.addEventListener('change', () => {this.paused=this.motion.matches||this.dataset.autoplay!=='true';this.finishTransition();this.updatePause();this.start();}, options);
      document.addEventListener('shopify:block:select', e => {
        const slide = e.target.closest?.('[data-slide]');
        if (slide && this.contains(slide)) {this.go(Number(slide.dataset.slide));this.paused=true;this.updatePause();this.stop();}
      }, options);
      this.finishTransition();this.updatePause();this.start();
    }
    updatePause() {if(this.pauseButton){this.pauseButton.textContent=this.paused?'▶':'Ⅱ';this.pauseButton.setAttribute('aria-label',this.paused?window.skyStrings.play:window.skyStrings.pause);this.pauseButton.setAttribute('aria-pressed',String(this.paused));}}
    finishTransition() {
      this.transitionId++;
      this.animations.forEach(animation=>animation.cancel());
      this.animations=[];this.transitioning=false;
      this.slides.forEach((slide,i)=>{const active=i===this.index;slide.hidden=!active;slide.inert=!active;slide.setAttribute('aria-hidden',String(!active));slide.classList.toggle('is-active',active);});
      this.dots.forEach((dot,i)=>dot.setAttribute('aria-pressed',String(i===this.index)));
      const nextImage=this.slides[(this.index+1)%this.slides.length]?.querySelector('img');
      if(nextImage){nextImage.fetchPriority='low';nextImage.loading='eager';}
    }
    go(index) {
      if (!this.slides.length) return;
      const next=((index%this.slides.length)+this.slides.length)%this.slides.length;
      if(next===this.index){this.start();return;}
      this.stop();this.finishTransition();
      const outgoing=this.slides[this.index];
      const direction=(index>this.index?1:-1)*(getComputedStyle(this).direction==='rtl'?-1:1);
      this.index=next;
      const incoming=this.slides[next];
      incoming.hidden=false;incoming.inert=false;incoming.setAttribute('aria-hidden','false');incoming.classList.add('is-active');
      outgoing.inert=true;outgoing.setAttribute('aria-hidden','true');outgoing.classList.remove('is-active');
      this.dots.forEach((dot,i)=>dot.setAttribute('aria-pressed',String(i===this.index)));
      if(this.motion.matches||typeof incoming.animate!=='function'){this.finishTransition();this.start();return;}
      this.transitioning=true;
      const token=++this.transitionId;
      const timing={duration:350,easing:'cubic-bezier(0.22, 1, 0.36, 1)',fill:'both'};
      this.animations=[
        outgoing.animate([{transform:'translateX(0)'},{transform:`translateX(${-direction*100}%)`}],timing),
        incoming.animate([{transform:`translateX(${direction*100}%)`},{transform:'translateX(0)'}],timing)
      ];
      Promise.all(this.animations.map(animation=>animation.finished)).then(()=>{
        if(this.isConnected&&this.transitionId===token){this.finishTransition();this.start();}
      }).catch(()=>{});
    }
    stop(){clearTimeout(this.timer);}
    start(){this.stop();if(this.visible&&!this.paused&&!this.transitioning&&!document.hidden&&this.slides.length>1&&!this.querySelector('.sg-slides')?.contains(document.activeElement)){this.timer=setTimeout(()=>this.go(this.index+1),Number(this.dataset.interval)||4000);}}
    disconnectedCallback(){this.stop();this.finishTransition();this.abort?.abort();this.visibilityObserver?.disconnect();}
  });
  if (!customElements.get('sky-auto-scroll')) customElements.define('sky-auto-scroll', class extends HTMLElement {
    connectedCallback() {
      this.track=this.querySelector('[data-scroll-track]');
      if(!this.track)return;
      this.abort=new AbortController();const options={signal:this.abort.signal};
      this.originals=[...this.track.children];
      this.button=this.querySelector('[data-motion-toggle]');
      this.motion=matchMedia('(prefers-reduced-motion: reduce)');
      this.paused=this.motion.matches||this.dataset.autoplay!=='true';
      this.visible=true;this.hovered=false;this.dragging=false;this.offset=0;
      this.speed=Math.max(10,Math.min(60,Number(this.dataset.speed)||25));
      this.button?.addEventListener('click',()=>{this.paused=!this.paused;this.updateButton();this.resume();},options);
      this.track.addEventListener('pointerenter',()=>{if(matchMedia('(hover: hover)').matches){this.hovered=true;this.stop();}},options);
      this.track.addEventListener('pointerleave',()=>{this.hovered=false;this.resume();},options);
      this.track.addEventListener('pointerdown',()=>{this.dragging=true;this.stop();},options);
      if (this.track.classList.contains('sg-brand-track')) {
        let pointer = null, originX, originY, initialScroll, moved = false;
        this.track.addEventListener('dragstart', event => event.preventDefault(), options);
        this.track.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0) return;
          pointer = event.pointerId;
          originX = event.clientX;
          originY = event.clientY;
          initialScroll = this.track.scrollLeft;
          moved = false;
        }, options);
        window.addEventListener('pointermove', event => {
          if (event.pointerId !== pointer || !this.distance) return;
          const delta = event.clientX - originX;
          if (!moved) {
            if (Math.abs(delta) < 5 || Math.abs(delta) < Math.abs(event.clientY - originY)) return;
            moved = true;
            this.track.setPointerCapture(pointer);
          }
          const sign = this.rtl ? -1 : 1;
          const position = (initialScroll - delta) * sign;
          this.track.scrollLeft = sign * ((position % this.distance + this.distance) % this.distance);
        }, options);
        const finish = event => { if (event.pointerId === pointer) pointer = null; };
        window.addEventListener('pointerup', finish, options);
        window.addEventListener('pointercancel', finish, options);
        this.track.addEventListener('click', event => {
          if (moved && event.detail) { event.preventDefault(); moved = false; }
        }, {...options, capture:true});
      }
      const endDrag=()=>{if(this.dragging){this.dragging=false;this.resume(1200);}};
      window.addEventListener('pointerup',endDrag,options);window.addEventListener('pointercancel',endDrag,options);
      this.track.addEventListener('wheel',()=>{this.stop();this.resume(1500);},{...options,passive:true});
      this.track.addEventListener('keydown',()=>this.stop(),options);
      this.track.addEventListener('focusin',()=>this.stop(),options);
      this.track.addEventListener('focusout',e=>{if(!this.track.contains(e.relatedTarget))this.resume();},options);
      document.addEventListener('visibilitychange',()=>document.hidden?this.stop():this.resume(),options);
      this.motion.addEventListener('change',()=>{this.paused=this.motion.matches||this.dataset.autoplay!=='true';this.updateButton();this.resume();},options);
      document.addEventListener('shopify:block:select',e=>{if(this.contains(e.target)){this.paused=true;this.updateButton();this.stop();}},options);
      if('IntersectionObserver' in window){this.observer=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.visible?this.resume():this.stop();});this.observer.observe(this);}
      if('ResizeObserver' in window){this.resizeObserver=new ResizeObserver(()=>this.setup());this.resizeObserver.observe(this.track);}
      this.setup();document.fonts?.ready.then(()=>{if(this.isConnected)this.setup();});
    }
    setup(){
      this.stop();this.track.querySelectorAll('[data-scroll-copy]').forEach(el=>el.remove());
      if(!this.originals.length)return;
      this.rtl=getComputedStyle(this.track).direction==='rtl';
      const gap=parseFloat(getComputedStyle(this.track).columnGap)||0;
      const width=this.originals.reduce((n,el)=>n+el.getBoundingClientRect().width+gap,0);
      if(!width)return;
      const groups=Math.max(1, Math.ceil(this.track.clientWidth/width));
      for(let g=0;g<groups;g++)this.originals.forEach(original=>{
        const clone=original.cloneNode(true);clone.dataset.scrollCopy='';clone.setAttribute('aria-hidden','true');
        [clone,...clone.querySelectorAll('*')].forEach(el=>{
          el.removeAttribute('id');el.removeAttribute('data-shopify-editor-block');
          if(el.matches('a,button,input,select,textarea,[tabindex]'))el.tabIndex=-1;
        });this.track.append(clone);
      });
      const copy=this.track.querySelector('[data-scroll-copy]');
      this.distance=Math.abs(this.originals[0].offsetLeft-copy.offsetLeft);
      this.offset=0;this.track.scrollLeft=0;this.updateButton();this.resume();
    }
    updateButton(){if(this.button){this.button.hidden=false;this.button.textContent=this.paused?window.skyStrings.play+' ▶':window.skyStrings.pause+' Ⅱ';this.button.setAttribute('aria-label',this.paused?window.skyStrings.play:window.skyStrings.pause);this.button.setAttribute('aria-pressed',String(this.paused));}}
    resume(delay=0){
      this.stop();
      if(delay){this.delay=setTimeout(()=>this.resume(),delay);return;}
      if(this.paused||!this.visible||document.hidden||this.hovered||this.dragging||!this.distance||this.track.querySelector(':focus-visible'))return;
      this.offset=Math.abs(this.track.scrollLeft)%this.distance;this.last=0;
      const step=time=>{
        if(this.last)this.offset=(this.offset+this.speed*Math.min(time-this.last,64)/1000)%this.distance;
        this.last=time;this.track.scrollLeft=this.rtl?-this.offset:this.offset;
        this.frame=requestAnimationFrame(step);
      };this.frame=requestAnimationFrame(step);
    }
    stop(){cancelAnimationFrame(this.frame);clearTimeout(this.delay);this.last=0;}
    disconnectedCallback(){this.stop();this.abort?.abort();this.observer?.disconnect();this.resizeObserver?.disconnect();this.track?.querySelectorAll('[data-scroll-copy]').forEach(el=>el.remove());}
  });
})();
